import { AddFilterBaseFunc, FilterContext, RemoveFilterBaseFunc } from "../core";
import { DashboardFilterKeys, Filters, IChartData, IDashboardDimensionsViewModel } from "./plotdata";

export type { IChartData, IDashboardDimensionsViewModel, Filters } from "./plotdata";
export { DashboardFilterKeys } from "./plotdata";
export type AddFilterFunc = AddFilterBaseFunc<typeof DashboardFilterKeys>;
export type RemoveFilterFunc = RemoveFilterBaseFunc<typeof DashboardFilterKeys>;

import * as DashWorker from "./worker";
export { DashWorker }

export interface IBrainDashboard {
	allData: boolean;
	references: IDashboardDimensionsViewModel;
	filters: Filters;
	chartData: IChartData;
	selectedFacilities: Set<string>;
    selectedHhses: Set<string>;
    onlyDataQualityEligible: boolean;
}


export const initState = (): IBrainDashboard => {
	return {
		allData: false,
		references: {},
		chartData: null,
		filters: {},
		selectedFacilities: new Set(),
		selectedHhses: new Set(),
		onlyDataQualityEligible: false,
	}
};


export const loadBrainDashboard = async (state: IBrainDashboard): Promise<IBrainDashboard> => {
	DashWorker.start(2010);
	try {
		await DashWorker.ping()
		console.log("WORKER PING RESOLVED");
		return await fetchData(state, state.allData);
	} catch {
		console.error("WORKER PING FAILED");
	}
}

export const clearFilters = (state: IBrainDashboard): IBrainDashboard => {
	const maybeSetDefaultFilter = (filterKey: string, dimensions: { name: string }[]): Set<string>  => {
		if (!dimensions || dimensions.length !== 1)
			return (new Set<string>());

		if (state.allData)
			return (new Set<string>());

		let filterSet = new Set<string>();
		filterSet.add(dimensions[0].name);

		state = addFilter(state)(filterKey, [dimensions[0].name])
		return filterSet;
	}

	state.filters = {};
	state.selectedHhses  = maybeSetDefaultFilter(DashboardFilterKeys.HHS.key, state.chartData.level1.hhses);
	state.selectedFacilities = maybeSetDefaultFilter(DashboardFilterKeys.Facility.key, state.chartData.level1.facilities);

	return { ... state };

}



export const closeBrainDashboard = () =>
	DashWorker.stop();

const fetchData = async (state: IBrainDashboard, all: boolean): Promise<IBrainDashboard> => {
	try {
		const data = await DashWorker.fetch(all);

		const maybeSetDefaultFilter = (filterKey: string, dimensions: { name: string }[]): Set<string>  => {
			if (!dimensions || dimensions.length !== 1)
				return (new Set<string>());

			if (all)
				return (new Set<string>());

			let filterSet = new Set<string>();
			filterSet.add(dimensions[0].name);

			state = addFilter(state)(filterKey, [dimensions[0].name])
			return filterSet;
		}

		state.selectedHhses  = maybeSetDefaultFilter(DashboardFilterKeys.HHS.key, data.chartData.level1.hhses);
        state.selectedFacilities = maybeSetDefaultFilter(DashboardFilterKeys.Facility.key, data.chartData.level1.facilities);
		state.allData = all;
		state.references = data.references;
		state.chartData = data.chartData;
		
		return state;
	} catch {
		console.error("WORKER FETCHDATA FAILED");
		return state;
	}
}

export const computeChartData = async (state: IBrainDashboard): Promise<IBrainDashboard>  => {
	try {
		const data = await DashWorker.chartData(state.filters);
		state.chartData = data;
		return {...state};
	} catch {
		console.error("WORKER COMPUTECHARTDATA FAILED")
		return state;
	}
}

export const addFilter = (state: IBrainDashboard) => (key: keyof typeof DashboardFilterKeys, value: string | string[], context?: FilterContext, labelOverride?: string): IBrainDashboard => {
	const filters: Filters = Object.assign({}, state.filters);
	const values = (typeof value === "string")
		? [value]
		: value;

	//Replace the existing path entry if we are replacing a multi-value filter
	//to do so: traverse to the parent
	const baseContext: FilterContext = context && ((context.values().length > 1) ? context.parent() : context) || new FilterContext();
	baseContext.push(values);

	filters[key] = ({ value: values, labelOverride, parts: baseContext.parts });

	state.filters = filters;

	return {...state};
}


export const removeFilter = (state: IBrainDashboard, key: keyof typeof DashboardFilterKeys, value?: string, labelOverride?: string): IBrainDashboard => {
	const filters: Filters = Object.assign({}, state.filters);
	const valueToRemove: string = value || null;
	const context = FilterContext.fromFilters(filters, key);
	const values = context.values();
	const newValue = (valueToRemove ? values.filter(p => p != valueToRemove): null);

	const n = context.parent() || new FilterContext();
	if (newValue) {
		n.push(newValue);
	}
	filters[key] = ({value: n.values(), labelOverride,  parts: n.parts});

	if (n.parts.length === 0) {
		delete filters[key];
	}

	state.filters = filters;
	return {...state};
}